import { Color, ResponsiveAttribute } from '../../basic';
import {
  GsStructBorderRadius,
  GsStructBorderStyle,
  GsStructBorderWidth,
  GsStructComponent,
  GsStructHorizontalAlignment,
  GsStructVerticalAlignment
} from '../components';

/**
 * Component types declaring a `fillHeight` input, so they can stretch to the block height when the
 * block has `fillComponentHeight` enabled. Used to guard `setInput`, which throws for undeclared inputs.
 */
export const FILL_HEIGHT_COMPONENT_TYPES: string[] = ['Image', 'Gallery'];

export interface GsStructBlock {
  /**
   * The width defined as a column amount (1-12) for each breakpoint
   * @default 12 (for all breakpoints)
   */
  columns: ResponsiveAttribute<number>;
  /** The priority of this block (if higher it gets displayed first) */
  priority?: number;
  /** The locale of the block */
  locale: string;
  /** The client id to identify the block for settings */
  clientId?: string;
  /** The components inside this block */
  components?: GsStructComponent<any>[];
  /** The background color for a whole block */
  backgroundColor: Color;
  /** The width of the border */
  borderWidth: GsStructBorderWidth;
  /** The style of the border */
  borderStyle?: GsStructBorderStyle;
  /** The color of the border */
  borderColor?: Color;
  /** The radius of the border */
  borderRadius?: GsStructBorderRadius;
  /** Whether to fill the whole height of the block with its components; responsive so it can be disabled per breakpoint (e.g. off on mobile where blocks stack and have no neighbour to match). */
  fillComponentHeight?: boolean | ResponsiveAttribute<boolean>;
  /**
   * The vertical alignment of components inside the block
   * @default GsStructVerticalAlignment.top
   * @requires fillComponentHeight to be false
   */
  verticalAlignment: GsStructVerticalAlignment;
  /**
   * The horizontal alignment of components inside the block
   * @default GsStructHorizontalAlignment.center
   */
  horizontalAlignment?: GsStructHorizontalAlignment;
  /**
   * Allow the components to neighbor inside the block if the space is sufficient
   * @requires fillComponentHeight to be false
   */
  allowComponentNeighboring?: boolean;
}

/**
 * Checks that a value has the shape of a GsStructBlock.
 * Guards against malformed or malicious block data from the clipboard or a block template.
 * @param value The value to check
 * @returns True if the value looks like a valid block
 */
export function isGsStructBlock(value: unknown): value is GsStructBlock {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  // Saved blocks may lack locale or backgroundColor (e.g. a block with only a text), so both are optional here
  return (
    typeof obj['columns'] === 'object' &&
    obj['columns'] !== null &&
    (obj['locale'] == null || typeof obj['locale'] === 'string') &&
    (obj['backgroundColor'] == null || typeof obj['backgroundColor'] === 'string') &&
    (obj['components'] == null || Array.isArray(obj['components']))
  );
}

/**
 * Parses serialized blocks, e.g. the `json` of a block template.
 * @param json The serialized `GsStructBlock[]`
 * @returns The blocks, or undefined if the JSON is no non-empty array of valid blocks
 */
export function parseGsStructBlocks(json: string): GsStructBlock[] | undefined {
  try {
    const blocks: unknown = JSON.parse(json);
    return Array.isArray(blocks) && blocks.length > 0 && blocks.every(isGsStructBlock) ? blocks : undefined;
  } catch {
    return undefined;
  }
}
